import React from "react";
import { Group } from "reakit/Group";
import { Rover, RoverProps } from "reakit/Rover";
import styled, { css } from "styled-components";
import { Region } from "./constants/regions";
import { DropdownHandler } from "./DropdownHandler";
import { useKeyboardNav } from "./hooks/useKeyboardNav";
import { usePortalDropdownCoords } from "./hooks/usePortalDropdownCoords";
import { Icon } from "./Icon";
import { Dropdown, DropdownStyledProps } from "./SelectMenu";
import { mediaQueries, style } from "./style";
import { Text } from "./Text";
import { focusOutline } from "./utils/focusOutlineStyle";

const ORDERED_CONTINENTS = ["North America", "Europe", "Asia", "Australia"];

const Container = styled.div`
  position: relative;
`;

const BaseItem = styled.div`
  display: flex;
  align-items: center;
  background: ${style.colors.white};
  padding: ${style.vars.whitespace.small} ${style.vars.whitespace.regular};
  cursor: pointer;

  &:active {
    background: ${style.colors.blue.lightest};
  }
`;

const Trigger = styled(BaseItem)`
  width: 100%;
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.dark};
  border-radius: ${style.vars.borderRadius.base};

  &:hover {
    border-color: ${style.colors.blue.dark};
  }

  ${mediaQueries.mediumMin} {
    width: ${style.vars.dropdown.width};
    padding-top: ${style.vars.whitespace.regular};
    padding-bottom: ${style.vars.whitespace.regular};
  }

  &:focus {
    ${focusOutline()}
  }
`;

const FlagColumn = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${style.vars.whitespace.regular};
`;

const FlagIcon = styled(Icon)`
  width: 3.125rem;
  height: 1.75rem;
`;

const NameColumn = styled.div`
  flex-grow: 1;
`;

export const SlugColumn = styled.div`
  font-weight: ${style.vars.fontWeight.bold};
`;

const ArrowIcon = styled(Icon)`
  justify-self: flex-end;
  position: relative;
  font-size: 0.75rem;
  margin-left: 1rem;
`;

type RegionSelectorLayoutProps = {
  $renderAsSingleColumn?: boolean;
};

const DropdownElement = styled(Group)<DropdownStyledProps & RegionSelectorLayoutProps>`
  background: ${style.colors.white};
  border: ${style.vars.borderWidth.thin} solid ${style.colors.blue.dark};
  margin-top: ${style.vars.whitespace.xsmall};
  border-radius: ${style.vars.borderRadius.base};

  ${mediaQueries.mediumMin} {
    position: absolute;
    ${(props) => {
      if (props.$shouldUseCoords) {
        return css`
          top: ${props.$coords.top}px;
          left: ${props.$coords.left}px;
          overflow-x: auto;
        `;
      } else {
        return css`
          left: 0;
          top: 100%;
          /*
           * Since this element is absolutely positioned, we need to define its
           * width explicitly (3 columns of items plus 2px to account for borders),
           * unless we render as a single column.
           */
          min-width: ${props.$renderAsSingleColumn ? style.vars.dropdown.width : `auto`};
        `;
      }
    }}

    display: ${(props) => (props.$renderAsSingleColumn ? "block" : "flex")};
  }

  ${mediaQueries.mediumMin} {
    ${({ $renderAsSingleColumn }) =>
      $renderAsSingleColumn &&
      css`
        max-height: ${style.vars.dropdown.maxHeight};
        overflow: auto;
        -webkit-overflow-scrolling: touch;
      `}
  }
`;

type RegionItemProps = {
  isSelected: boolean;
};

const RoverBaseItem: React.FC<RoverProps> = ({ children, ...rest }) => {
  return (
    <Rover as={BaseItem} {...rest}>
      {children}
    </Rover>
  );
};

const RegionItem = styled(RoverBaseItem)<RoverProps & RegionItemProps>`
  &:hover {
    background: ${style.colors.grey.light};
  }

  &:focus {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -${style.vars.whitespace.xsmall};
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      &,
      &:hover {
        color: ${style.colors.white};
        background: ${style.colors.grey.dark};
      }
      &:focus {
        outline-color: ${style.colors.white};
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: ${style.vars.disabledOpacity};
      pointer-events: none;
    `}
`;

const RegionGroup = styled(Group)<RegionSelectorLayoutProps>`
  ${mediaQueries.mediumMin} {
    position: relative;
    min-width: ${style.vars.dropdown.width};

    /*
     * For the last RegionItem in the first RegionGroup: prevent the item's
     * corner from cutting into the Dropdown element's border.
     */
    &:first-of-type ${RegionItem}:last-of-type {
      border-bottom-left-radius: ${style.vars.borderRadius.base};
    }

    ${({ $renderAsSingleColumn }) =>
      !$renderAsSingleColumn &&
      css`
        &:after {
          content: "";
          position: absolute;
          top: ${style.vars.whitespace.small};
          right: 0;
          bottom: ${style.vars.whitespace.small};
          border-right: ${style.vars.borderWidth.thin} solid ${style.colors.blue.dark};
        }

        &:last-of-type:after {
          content: none;
        }
      `}
  }
`;

const GroupLabel = styled.div`
  font-weight: ${style.vars.fontWeight.bold};
  color: ${style.colors.grey.dark};
  padding: ${style.vars.whitespace.small} ${style.vars.whitespace.regular} 0;
`;

type Props = {
  id?: string;
  regions: Region[];
  selectedRegion: Region;
  setSelectedRegion: React.Dispatch<Region>;
  renderInPortal?: boolean;
  renderAsSingleColumn?: boolean;
  className?: string;
};

/**
 * @deprecated Use `RegionSelectorV2` instead
 */
export const RegionSelector: React.FC<Props> = ({
  id,
  regions,
  selectedRegion,
  setSelectedRegion,
  renderInPortal = false,
  renderAsSingleColumn = false,
  className,
}) => {
  const { flag: selectedFlag, label: selectedLabel, slug: selectedSlug } = selectedRegion;

  const groupedByContinent = regions.reduce((acc, region) => {
    const { continent } = region;

    if (acc[continent]) {
      acc[continent].push(region);
    } else {
      acc[continent] = [region];
    }

    return acc;
  }, {} as Record<string, Region[]>);

  const continentsWithRegions = ORDERED_CONTINENTS.map((continent) => ({
    continentLabel: continent,
    regionList: groupedByContinent[continent],
  }));

  const { isMenuOpen, menuRef, onKeyUp, onKeyDown, onItemKeyUp, onClick, close, rover } = useKeyboardNav({ isMenuOpen: false });
  const { coords, updateCoords, shouldUseCoords } = usePortalDropdownCoords({
    menuRef,
    isMenuOpen,
    renderInPortal,
    includeWidth: false,
  });

  return (
    <Container className={className}>
      <Trigger
        id={id}
        role="button"
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        onClick={onClick}
        tabIndex={0}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        ref={menuRef}
      >
        <FlagColumn>
          <FlagIcon icon={`flag-${selectedFlag}`} />
        </FlagColumn>
        <NameColumn>{selectedLabel}</NameColumn>
        <SlugColumn>{selectedSlug.toUpperCase()}</SlugColumn>
        <ArrowIcon icon={isMenuOpen ? "arrow-up" : "arrow-down"} />
      </Trigger>
      <DropdownHandler
        mobileAlignment="fixed-bottom"
        mobileTitle="Select a Region"
        clickAway={(e) => {
          close();
          e.stopPropagation();
        }}
        inProp={isMenuOpen}
        renderInPortal={renderInPortal}
      >
        <Dropdown coords={coords} updateCoords={updateCoords} shouldUseCoords={shouldUseCoords}>
          <DropdownElement
            className="dropdown-wrap"
            role="menu"
            aria-label="Select a Region"
            aria-orientation="vertical"
            $renderAsSingleColumn={renderAsSingleColumn}
          >
            {continentsWithRegions.map(({ continentLabel, regionList }) => {
              // only return groups (continents) with regions in them
              if (regionList && regionList.length > 0) {
                return (
                  <RegionGroup key={continentLabel} aria-label={continentLabel} $renderAsSingleColumn={renderAsSingleColumn}>
                    <GroupLabel>
                      <Text type="small">{continentLabel}</Text>
                    </GroupLabel>
                    {regionList.map((region) => (
                      <RegionItem
                        key={region.slug}
                        role="menuitem"
                        isSelected={region.slug === selectedRegion?.slug}
                        disabled={!!region.restriction}
                        onClick={() => {
                          setSelectedRegion(region);
                          close();
                        }}
                        onKeyDown={onKeyDown}
                        onKeyUp={onItemKeyUp}
                        {...rover}
                      >
                        <FlagColumn>
                          <FlagIcon icon={`flag-${region.flag}`} />
                        </FlagColumn>
                        <NameColumn>{region.label}</NameColumn>
                        <SlugColumn>{region.slug.toUpperCase()}</SlugColumn>
                      </RegionItem>
                    ))}
                  </RegionGroup>
                );
              }
            })}
          </DropdownElement>
        </Dropdown>
      </DropdownHandler>
    </Container>
  );
};
